import { useMemo } from 'react';

import { cn } from '@crossnokaye/ux-common';

import { ButtonComponentActionConfigVariant, DropdownOption } from '@repo/sdk/models/components';

import { analytics } from '@/analytics/analytics-service';
import { DropdownCell, type DropdownCellOption } from '@/components/data-table';
import { getRefValue, mapLegacyButtonVariantToClassName } from '@/features/equipment-browser/utils';
import { PRIVILEGES } from '@/hooks/access/access-table';
import { useActionPermission } from '@/hooks/use-action-permission';

import { ActionIcon } from './action-icon';
import { ActionComponentProps } from './types';
import { useHandleUpdateSettings } from './use-handle-update-settings';

/** Cell option carrying the SDK actions needed to apply a selection. */
type ActionDropdownOption = DropdownCellOption & {
  actions?: DropdownOption['actions'];
};

export const ActionDropdown = ({
  state,
  disabled,
  analyticsAttributes,
  className,
  ...buttonProps
}: ActionComponentProps) => {
  const { isDisabled: disabledByPermissions, tooltipText: permissionTooltipText } =
    useActionPermission({
      // TODO: UX-3355: We probably need to specify permissions in the dropdown config
      privilege: PRIVILEGES.writeCommands,
    });

  const { handleUpdateSettings, isPending } = useHandleUpdateSettings();

  const dropdownConfig = state?.actionComponentConfig?.dropdownComponentActionConfig;

  const currentValue = dropdownConfig?.valueRef
    ? getRefValue(dropdownConfig.valueRef)?.toString()
    : undefined;

  const handleSelect = (option: ActionDropdownOption) => {
    const currentLabel =
      dropdownConfig?.options?.find((dropdownOption) => dropdownOption.value === currentValue)
        ?.label ?? currentValue;

    analytics.track('Device Mode Changed', {
      ...analyticsAttributes,
      'Previous Device Mode': currentLabel,
      'Device Mode': option.label,
    });

    handleUpdateSettings(option.actions?.settingActions || []);
  };

  const showIcon = dropdownConfig?.showIcon;
  const options = useMemo<ActionDropdownOption[]>(
    () =>
      (dropdownConfig?.options ?? []).map((option) => ({
        value: option.value,
        label: option.label,
        actions: option.actions,
        icon:
          showIcon && option.icon?.icon ? (
            <ActionIcon
              icon={option.icon.icon}
              variant={option.icon.variant}
              fallbackIcon="ShieldQuestion"
            />
          ) : undefined,
      })),
    [dropdownConfig?.options, showIcon]
  );

  const disabledButton = isPending || dropdownConfig?.disabled || disabled;
  const disabledActions = disabledByPermissions;

  const buttonVariant = dropdownConfig?.buttonVariant ?? ButtonComponentActionConfigVariant.Default;

  const tooltipContent = dropdownConfig?.disabled
    ? 'The fault cannot be reset until system conditions are met.'
    : disabledByPermissions
      ? permissionTooltipText
      : undefined;

  return (
    <DropdownCell<ActionDropdownOption>
      {...buttonProps}
      className={cn(mapLegacyButtonVariantToClassName(buttonVariant), className)}
      disabled={disabledButton}
      disableItems={disabledActions}
      onSelect={handleSelect}
      options={options}
      stateName={state?.stateName}
      subtitle={dropdownConfig?.subtitle}
      tooltipContent={tooltipContent}
      value={currentValue}
    />
  );
};
